import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  GraduationCap, Users, Clock, ArrowLeft, Plus, BookOpen, Award, CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const TrainingProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "technical",
    duration_hours: "",
    provider: "",
    cost: "0",
    mandatory: false
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/training/programs`, { headers });
        setPrograms(res.data.programs || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/training/programs`, {
        ...formData,
        duration_hours: parseInt(formData.duration_hours),
        cost: parseFloat(formData.cost || 0)
      }, { headers });
      toast.success("Training program created");
      setShowForm(false);
      const res = await axios.get(`${API}/admin/training/programs`, { headers });
      setPrograms(res.data.programs || []);
    } catch (error) {
      toast.error("Failed to create program");
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "Free";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
  };

  const getCategoryBadge = (category) => {
    const colors = {
      technical: "bg-blue-500/20 text-blue-400",
      compliance: "bg-red-500/20 text-red-400",
      leadership: "bg-purple-500/20 text-purple-400",
      "soft-skills": "bg-green-500/20 text-green-400"
    };
    return colors[category] || "bg-slate-500/20 text-slate-400";
  };

  const totalEnrollments = programs.reduce((sum, p) => sum + (p.enrollments || 0), 0);
  const totalCompletions = programs.reduce((sum, p) => sum + (p.completions || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Training Programs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="training-programs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Training Programs
            </h1>
            <p className="text-slate-400 text-sm">Manage employee learning and development</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Program
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <BookOpen className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{programs.length}</p>
            <p className="text-xs text-slate-400">Total Programs</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{totalEnrollments}</p>
            <p className="text-xs text-slate-400">Total Enrollments</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Award className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{totalCompletions}</p>
            <p className="text-xs text-slate-400">Completions</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <CheckCircle className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{programs.filter(p => p.mandatory).length}</p>
            <p className="text-xs text-slate-400">Mandatory</p>
          </CardContent>
        </Card>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-white">Create Training Program</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Program Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-sovereign"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white h-20"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Category</label>
                    <select
                      className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="technical">Technical</option>
                      <option value="compliance">Compliance</option>
                      <option value="leadership">Leadership</option>
                      <option value="soft-skills">Soft Skills</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Duration (hours)</label>
                    <Input
                      type="number"
                      value={formData.duration_hours}
                      onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Provider</label>
                    <Input
                      value={formData.provider}
                      onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Cost ($)</label>
                    <Input
                      type="number"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.mandatory}
                    onChange={(e) => setFormData({ ...formData, mandatory: e.target.checked })}
                    className="rounded border-[#c9a227]/50"
                  />
                  <label className="text-sm text-slate-400">Mandatory for all employees</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Create</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.map((program) => (
          <Card key={program.id} className="glass-card hover:border-[#c9a227]/40 transition-all">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-white text-lg">{program.name}</CardTitle>
                {program.mandatory && (
                  <Badge className="bg-red-500/20 text-red-400">Required</Badge>
                )}
              </div>
              <Badge className={getCategoryBadge(program.category)}>{program.category}</Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-400 text-sm line-clamp-2">{program.description}</p>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Duration:</span>
                  <span className="text-white ml-1">{program.duration_hours}h</span>
                </div>
                <div>
                  <span className="text-slate-500">Cost:</span>
                  <span className="text-[#c9a227] ml-1">{formatCurrency(program.cost)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Provider:</span>
                  <span className="text-white ml-1">{program.provider}</span>
                </div>
                <div>
                  <span className="text-slate-500">Enrolled:</span>
                  <span className="text-blue-400 ml-1">{program.enrollments || 0}</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">Completion Rate</span>
                  <span className="text-[#c9a227]">
                    {program.enrollments ? Math.round((program.completions / program.enrollments) * 100) : 0}%
                  </span>
                </div>
                <Progress 
                  value={program.enrollments ? (program.completions / program.enrollments) * 100 : 0} 
                  className="h-2"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TrainingProgramsPage;
